import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

let ffmpegInstance: FFmpeg | null = null;
let ffmpegLoading: Promise<FFmpeg> | null = null;

function isMp4Blob(blob: Blob): boolean {
  return /^video\/mp4/i.test(blob.type);
}

function inputNameForBlob(blob: Blob): string {
  const name = blob instanceof File ? blob.name : '';
  const extMatch = name.match(/\.[a-z0-9]+$/i);
  if (extMatch) {
    return `input${extMatch[0].toLowerCase()}`;
  }
  if (/quicktime/i.test(blob.type)) {
    return 'input.mov';
  }
  if (/webm/i.test(blob.type)) {
    return 'input.webm';
  }
  if (/mp4|m4v/i.test(blob.type)) {
    return 'input.mp4';
  }
  return 'input.mov';
}

async function getFfmpeg(): Promise<FFmpeg> {
  if (ffmpegInstance?.loaded) {
    return ffmpegInstance;
  }
  if (ffmpegLoading) {
    return ffmpegLoading;
  }

  ffmpegLoading = (async () => {
    const ffmpeg = new FFmpeg();
    const base = `${window.location.origin}/assets/ffmpeg`;
    await ffmpeg.load({
      coreURL: await toBlobURL(`${base}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${base}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    ffmpegInstance = ffmpeg;
    return ffmpeg;
  })();

  try {
    return await ffmpegLoading;
  } catch (error) {
    ffmpegLoading = null;
    throw error;
  }
}

/**
 * Ensure a recorded blob is seekable MP4 (H.264 + faststart).
 * Chrome MediaRecorder usually yields WebM; Safari may already give MP4.
 */
export async function ensureMp4Blob(input: Blob): Promise<Blob> {
  if (isMp4Blob(input)) {
    return input.type === 'video/mp4' ? input : new Blob([input], { type: 'video/mp4' });
  }

  return transcodeToMp4(input, 'input.webm');
}

export type ConvertProgress = {
  stage: 'loading' | 'preparing' | 'converting' | 'finalizing';
  /** 0-1 while converting; 0 otherwise. */
  progress: number;
};

/**
 * Convert a camera-roll file (often iPhone HEVC .MOV) to seekable H.264 MP4.
 */
export async function convertUploadToMp4(
  input: Blob,
  onProgress?: (status: ConvertProgress) => void
): Promise<Blob> {
  return transcodeToMp4(input, inputNameForBlob(input), onProgress);
}

async function transcodeToMp4(
  input: Blob,
  inputName: string,
  onProgress?: (status: ConvertProgress) => void
): Promise<Blob> {
  onProgress?.({ stage: 'loading', progress: 0 });
  const ffmpeg = await getFfmpeg();
  const outputName = 'output.mp4';

  onProgress?.({ stage: 'preparing', progress: 0 });
  await ffmpeg.writeFile(inputName, await fetchFile(input));

  const onFfmpegProgress = ({ progress }: { progress: number }) => {
    const clamped = Number.isFinite(progress) ? Math.min(0.99, Math.max(0, progress)) : 0;
    onProgress?.({ stage: 'converting', progress: clamped });
  };
  ffmpeg.on('progress', onFfmpegProgress);

  const attempts: string[][] = [
    [
      '-i', inputName,
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-pix_fmt', 'yuv420p',
      '-vf', 'scale=1920:1080:force_original_aspect_ratio=decrease',
      '-movflags', '+faststart',
      '-an',
      outputName,
    ],
    [
      '-i', inputName,
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-pix_fmt', 'yuv420p',
      '-movflags', '+faststart',
      '-an',
      outputName,
    ],
    [
      '-i', inputName,
      '-c', 'copy',
      '-an',
      '-movflags', '+faststart',
      outputName,
    ],
  ];

  let lastCode = -1;
  try {
    for (const args of attempts) {
      await safeDelete(ffmpeg, outputName);
      lastCode = await ffmpeg.exec(args);
      if (lastCode === 0) {
        break;
      }
    }
  } finally {
    ffmpeg.off('progress', onFfmpegProgress);
  }

  if (lastCode !== 0) {
    await safeDelete(ffmpeg, inputName);
    await safeDelete(ffmpeg, outputName);
    throw new Error(`ffmpeg exited with code ${lastCode}`);
  }

  onProgress?.({ stage: 'finalizing', progress: 1 });
  const data = await ffmpeg.readFile(outputName);
  await safeDelete(ffmpeg, inputName);
  await safeDelete(ffmpeg, outputName);

  const bytes = data instanceof Uint8Array ? data : new TextEncoder().encode(String(data));
  return new Blob([bytes], { type: 'video/mp4' });
}

async function safeDelete(ffmpeg: FFmpeg, name: string) {
  try {
    await ffmpeg.deleteFile(name);
  } catch {
    // ignore cleanup failures
  }
}

export function pickRecorderMimeType(): string {
  const candidates = [
    'video/mp4;codecs=avc1.42E01E',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  return candidates.find(type => MediaRecorder.isTypeSupported(type)) ?? '';
}
